<template>
	<input
		class="styled-range"
		:class="{ disabled }"
		type="range"
		@click="onClick"
		@input="$emit('input', $event.target.value)"
		@change="$emit('change', $event.target.value)"
		:max="max"
		:disabled="disabled"
		step="0.001"
		ref="range"
	/>
</template>

<script>
export default {
	name: 'StyledRange',
	props: {
		value: null,
		max: Number,
		disabled: { type: Boolean, default: null },
	},
	mounted() {
		this.$refs.range.value = this.value
	},
	watch: {
		value(value) {
			this.$refs.range.value = value
		},
	},
	methods: {
		onClick(e) {
			const percent = e.offsetX / e.target.clientWidth
			const value = this.max * percent
			this.$emit('change', value)
		},
	},
	emits: ['input', 'change']
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

// Stole these scss overrides from https://github.com/darlanrod/input-range-sass
$track-color: #eceff1 !default;
$thumb-color: white !default;

$thumb-radius: 12px !default;
$thumb-height: 24px !default;
$thumb-width: 24px !default;

$thumb-shadow-x: 0 !default;
$thumb-shadow-y: 6px !default;
$thumb-shadow-blur: 13px !default;
$thumb-shadow-color: rgba(0, 0, 0, 0.2) !default;
$thumb-border-width: 0 !default;
$thumb-border-color: $outline-gray !default;

$track-width: 100% !default;
$track-height: 4px !default;
$track-shadow-x: 0 !default;
$track-shadow-y: 0 !default;
$track-shadow-blur: 0 !default;
$track-shadow-color: rgba(0, 0, 0, 0.2) !default;
$track-border-width: 0 !default;
$track-border-color: transparent !default;

$track-radius: 5px !default;
$contrast: 5% !default;

@mixin shadow($shadow-x, $shadow-y, $shadow-blur, $shadow-color) {
	box-shadow: $shadow-x $shadow-y $shadow-blur $shadow-color;
}

@mixin track {
	height: $track-height;
	transition: all 0.2s ease;
	width: $track-width;
}

@mixin thumb {
	box-shadow: 0px 6px 13px 0px #0000001f, 0px 0.5px 4px 0px #0000001f;
	background: $thumb-color;
	border: $thumb-border-width solid $thumb-border-color;
	border-radius: $thumb-radius;
	box-sizing: border-box;
	cursor: default;
	height: $thumb-height;
	width: $thumb-width;
	&:hover {
		background: lighten($thumb-color, 0.2);
	}
}

.styled-range {
	-webkit-appearance: none;
	background: transparent;
	width: $track-width;
	cursor: pointer;

	&::-moz-focus-outer {
		border: 0;
	}
	&:focus::-webkit-slider-runnable-track,
	&::-webkit-slider-runnable-track {
		@include track;
		@include shadow(
			$track-shadow-x,
			$track-shadow-y,
			$track-shadow-blur,
			$track-shadow-color
		);
		border: $track-border-width solid $track-border-color;
		border-radius: $track-radius;
	}

	&::-webkit-slider-thumb {
		@include thumb;
		-webkit-appearance: none;
		margin-top: -10px;
		transition: opacity 100ms ease-in-out;
	}

	&::-moz-range-track {
		@include shadow(
			$track-shadow-x,
			$track-shadow-y,
			$track-shadow-blur,
			$track-shadow-color
		);
		@include track;
		border: $track-border-width solid $track-border-color;
		border-radius: $track-radius;
	}

	&::-moz-range-thumb {
		@include thumb;
		transition: opacity 100ms ease-in-out;
	}

	&:disabled {
		&::-webkit-slider-thumb {
			opacity: 0;
		}
		&::-moz-range-thumb {
			opacity: 0;
		}
	}
}
</style>
